import {
  cloneDataOrNull, DataModel, DataModelBuilder, isSameData, isSameList,
  SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { CustomerProfile } from '../customer-profile.js';
import { PaymentMethod } from '../payment-method.js';
import { PaymentType } from '../payment-type.js';
import { CartItemDto } from './cart-item.js';
import { TicketType } from '../ticket-type.js';
import { ElectronicDeliveryMethod } from '../electronic-delivery-method.js';
import { VersionRange } from '../../core/version-range.js';
import { Language } from '../language.js';
import { OrderCreationStrategy } from './order-creation-strategy.js';
import { Result } from '../../../utility/result.js';
import { OfflineEventCartEntryDto } from './offline-event-cart-entry.js';
import { OrderDomain } from '../order-domain.js';
import { OfflineReceiptType } from '../invoicing/offline-receipt-type.js';
import { CartCustomerDto } from './cart-customer.js';
import { OfflineCartBase } from './offline-cart-base.js';

export class OfflineEventCartDto extends DataModel implements OfflineCartBase {
  static readonly TYPE_ID: string = '03ae5313740644e091e51ac8d32c6316';
  static readonly SCHEMA_VERSION: number = 10;

  private constructor(
      public readonly businessRef: BusinessModuleRef,
      public readonly creatorId: string,
      public readonly entries: OfflineEventCartEntryDto[],
      public readonly customerId: string | null,
      public readonly customer: CartCustomerDto | null,
      public readonly paymentType: PaymentType,
      public readonly paymentMethod: PaymentMethod,
      public readonly language: Language,
      public readonly domain: OrderDomain,
      public readonly strategy: OrderCreationStrategy,
      public readonly allowOfflineOrderReceipts: boolean | null,
      public readonly receiptType: OfflineReceiptType | null,
  ) {
    super();
  }

  get $typeId() {
    return OfflineEventCartDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(OfflineEventCartDto.SCHEMA_VERSION);
  }

  static create(args: {
      businessRef: BusinessModuleRef,
      creatorId: string,
      entries: OfflineEventCartEntryDto[],
      customerId: string | null,
      customer: CartCustomerDto | null,
      paymentType: PaymentType,
      paymentMethod: PaymentMethod,
      language: Language,
      domain: OrderDomain,
      strategy: OrderCreationStrategy,
      allowOfflineOrderReceipts: boolean | null,
      receiptType: OfflineReceiptType | null,
  }): OfflineEventCartDto {
    return new OfflineEventCartDto(
        args.businessRef,
        args.creatorId,
        args.entries,
        args.customerId,
        args.customer,
        args.paymentType,
        args.paymentMethod,
        args.language,
        args.domain,
        args.strategy,
        args.allowOfflineOrderReceipts,
        args.receiptType,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineEventCartDto {
    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      entries: MapUtils.getList<OfflineEventCartEntryDto>(map, 'entries', (e) => {
        return context.fromData(e as SerializedData);
      }),
      creatorId: MapUtils.getString(map, 'creatorId'),
      customerId: MapUtils.getStringOrNull(map, 'customerId'),
      customer: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'customer')),
      paymentType: toEnumFromString(MapUtils.getString(map, 'paymentType'), PaymentType),
      paymentMethod: toEnumFromString(MapUtils.getString(map, 'paymentMethod'), PaymentMethod),
      language: toEnumFromString(MapUtils.getString(map, 'language'), Language),
      domain: toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
      strategy: toEnumFromString(MapUtils.getString(map, 'strategy'), OrderCreationStrategy),
      allowOfflineOrderReceipts: MapUtils.getBooleanOrNull(map, 'allowOfflineOrderReceipts'),
      receiptType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'receiptType'), OfflineReceiptType),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'entries': this.entries.map((e) => context.toData(e)),
      'creatorId': this.creatorId,
      'customerId': this.customerId,
      'customer': context.toDataOrNull(this.customer),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'language': fromEnumAsString(this.language),
      'domain': fromEnumAsString(this.domain),
      'strategy': fromEnumAsString(this.strategy),
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
      'receiptType': fromEnumAsStringOrNull(this.receiptType),
    };
  }

  clone(): this {
    return OfflineEventCartDto.create({
      businessRef: this.businessRef.clone(),
      entries: this.entries.map((e) => e.clone()),
      creatorId: this.creatorId,
      customerId: this.customerId,
      customer: cloneDataOrNull(this.customer),
      paymentType: this.paymentType,
      paymentMethod: this.paymentMethod,
      language: this.language,
      domain: this.domain,
      strategy: this.strategy,
      allowOfflineOrderReceipts: this.allowOfflineOrderReceipts,
      receiptType: this.receiptType,
    }) as this;
  }

  isSame(other: OfflineEventCartDto): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.entries, this.entries, isSameData) &&
      other.creatorId === this.creatorId &&
      other.customerId === this.customerId &&
      other.paymentType === this.paymentType &&
      other.paymentMethod === this.paymentMethod &&
      other.language === this.language &&
      other.domain === this.domain &&
      other.strategy === this.strategy &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts &&
      other.receiptType === this.receiptType &&
      isSameData(other.customer, this.customer);
  }

  findUniqueEventId(): Result<string | null, 'multiple-events-found'> {
    let eventId: string | null = null;

    for(const entry of this.entries) {
      if(entry.eventId != null) {
        if(eventId != null && entry.eventId !== eventId) {
          return Result.error('multiple-events-found');
        }

        eventId = entry.eventId;
      }
    }

    return Result.ok(eventId);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OfflineEventCartDto;
  }

  static assertType(order: DataModel): order is OfflineEventCartDto {
    return order instanceof OfflineEventCartDto;
  }
}

export class OfflineEventCartDtoBuilder extends DataModelBuilder<OfflineEventCartDto> {
  build(context: SerializationContext, map: SerializedData): OfflineEventCartDto {
    return OfflineEventCartDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineEventCartDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineEventCartDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    // - add `discount` field of type `Discount | null`.
    if(version < 2) {
      version = 2;
    }

    /**
     * Version 3:
     * - change `eventId` from {@link string} string` to {@link string | null}.
     */
    if(version < 3) {
      version = 3;
    }

    // In version 4 we added `language` of type `Language`.
    if(version < 4) {
      map['language'] = fromEnumAsString(Language.English);
      version = 4;
    }

    // In version 5 we added `strategy` of type `OrderCreationStrategy`.
    if(version < 5) {
      map['strategy'] = fromEnumAsString(OrderCreationStrategy.GenericOrder);
      version = 5;
    }

    // In version 6 we added the concept of cart entries.
    if(version < 6) {
      const entry = OfflineEventCartEntryDto.fromLegacy({
        eventId: MapUtils.getStringOrNull(map, 'eventId'),
        items: MapUtils.getList<CartItemDto>(map, 'items', (e) => {
          return context.fromData(e as SerializedData);
        }),
        voucher: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'voucher')),
        discount: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'discount')),
        supplementaryTicket: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'supplementaryTicket')),
        ticketType: toEnumFromString(MapUtils.getString(map, 'ticketType'), TicketType),
        requireTaxIdentifier: MapUtils.getBoolean(map, 'requireTaxIdentifier'),
        promoterId: MapUtils.getStringOrNull(map, 'promoterId'),
        note: MapUtils.getStringOrNull(map, 'note'),
        deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      });

      map['entries'] = [context.toData(entry)];

      version = 6;
    }

    // In version 7 we added `domain` of type `OrderDomain`.
    if(version < 7) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 7;
    }

    // In version 8 we added `allowOfflineOrderReceipts` of type `boolean | null`.
    if(version < 8) {
      map['allowOfflineOrderReceipts'] = null;
      version = 8;
    }

    // In version 9 we added `receiptType` of type `OfflineReceiptType | null`.
    if(version < 9) {
      map['receiptType'] = null;
      version = 9;
    }

    /*
    * In version 10:
    * - we added `customerId` of type `string | null`.
    * - we changed `customer` from type `CustomerProfile` to `CartCustomerDto | null`.
    */
    if(version < 10) {
      const legacyCustomer = context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer'));

      // Unlike online carts, here we always want to use the customer ID.
      // In older admin versions we always create the customer
      // before making the API call so it's safe to pass the customer ID only.
      if(legacyCustomer != null) {
        map['customerId'] = legacyCustomer.uniqueId;
        map['customer'] = null;
      }
      else {
        map['customerId'] = null;
        map['customer'] = null;
      }

      version = 10;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
